import {
  AdditiveBlending,
  BufferGeometry,
  type Color,
  CylinderGeometry,
  DataTexture,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  OctahedronGeometry,
  Points,
  PointsMaterial,
  RGBAFormat,
  type Scene,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import { buildLevelFromPlacements, randomSpec } from '../editor/placement';
import { loadArcaneMaterials } from './materials';
import { mergeStaticMeshes } from './meshMerge';
import { makeSpire } from './ramps';
import type { BuiltLevel } from './TestLevel';

/**
 * "Arcane Academy" — the game's first themed course: a floating magical academy
 * over a sea of slow lava. The surfable course is the editor's procedural Generate
 * backbone (a fixed seed → a stable ~300-ramp course of every ramp type, fanned
 * into branches). It is skinned in glowing crystal that comes in three hues per
 * lane with a view-dependent fresnel rim-glow, and surrounded by hand-placed,
 * non-collidable arcane decor: the Heart Crystal beacon, a ring of crystal towers,
 * floating rune gates + a finish portal, plus drifting motes, orbiting crystal
 * shards, will-o-wisp orbs and rising light shafts. All procedural (no assets),
 * emissive so the bloom pass makes it glow. Untimed free-surf (no zones).
 */

/** Fixed seed so the generated course is identical every load (a shippable map). */
const SEED = 0x5ca1ab1e;
const FEATURE_COUNT = 300; // randomSpec caps here; fans into ~4 branches → ~300 ramps
/** Separate fixed seed for the decorative scatter, so the look is stable + testable. */
const DECOR_SEED = 0xa0cadec0;

/** Deterministic PRNG (mulberry32) so the "random" course/decor is stable. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function emissive(color: number, intensity: number, opacity = 1): MeshStandardMaterial {
  return new MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: intensity,
    roughness: 0.3,
    metalness: 0.1,
    transparent: opacity < 1,
    opacity,
  });
}

/**
 * Inject a view-dependent fresnel rim-glow into a MeshStandardMaterial (the crystal
 * "energy edge" look). Self-contained: declares its own varyings in the vertex stage
 * (so it's robust across geometry/merge), and adds the rim term to the standard
 * `totalEmissiveRadiance` so the existing bloom pass amplifies it for free.
 */
function addFresnelGlow(mat: MeshStandardMaterial, glow: Color, power = 2.4): void {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uFresnelColor = { value: glow };
    shader.uniforms.uFresnelPower = { value: power };
    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'varying vec3 vGlowN;\nvarying vec3 vGlowV;\nvoid main() {')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vGlowN = normalize(normalMatrix * normal);
        vGlowV = normalize(-(modelViewMatrix * vec4(position, 1.0)).xyz);`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        'uniform vec3 uFresnelColor;\nuniform float uFresnelPower;\nvarying vec3 vGlowN;\nvarying vec3 vGlowV;\nvoid main() {',
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        float fres = pow(1.0 - abs(dot(normalize(vGlowN), normalize(vGlowV))), uFresnelPower);
        totalEmissiveRadiance += uFresnelColor * fres;`,
      );
  };
  mat.needsUpdate = true;
}

/** Three crystal-hue ramp variants (cloned off the base) with a fresnel rim-glow. */
function makeRampVariants(base: MeshStandardMaterial): MeshStandardMaterial[] {
  const hues = [
    { color: 0x9cc8ee, em: 0x3aa0ff }, // arcane cyan (the base)
    { color: 0xc2a8ff, em: 0x7a4cff }, // amethyst
    { color: 0x9af0dc, em: 0x18d0a8 }, // teal
  ];
  return hues.map(({ color, em }) => {
    const m = base.clone(); // keep the procedural grain map + roughness
    m.color.setHex(color);
    m.emissive.setHex(em);
    addFresnelGlow(m, m.emissive);
    return m;
  });
}

/** Add a decorative (non-collidable) mesh and return it. */
function decor(
  scene: Scene,
  geometry: Mesh['geometry'],
  material: MeshStandardMaterial,
  pos: Vector3,
): Mesh {
  const mesh = new Mesh(geometry, material);
  mesh.position.copy(pos);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  scene.add(mesh);
  return mesh;
}

/** A soft round additive sprite (procedural radial gradient) — node-safe (no canvas). */
function softDot(size = 32): DataTexture {
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - c) / c;
      const dy = (y - c) / c;
      const fall = Math.max(0, 1 - Math.sqrt(dx * dx + dy * dy));
      const a = Math.round(255 * fall * fall);
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = a;
    }
  }
  const tex = new DataTexture(data, size, size, RGBAFormat);
  tex.needsUpdate = true;
  return tex;
}

/** Drifting arcane motes: a soft additive point cloud filling the course volume. */
function makeMotes(center: Vector3, rng: () => number): Points {
  const N = 1500;
  const pos = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    const a = rng() * Math.PI * 2;
    const r = 500 + rng() * 6200;
    pos[i * 3] = Math.cos(a) * r;
    pos[i * 3 + 1] = (rng() - 0.5) * 5200;
    pos[i * 3 + 2] = Math.sin(a) * r;
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(pos, 3));
  const mesh = new Points(
    g,
    new PointsMaterial({
      color: 0xbfe6ff,
      size: 46,
      map: softDot(),
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.85,
      blending: AdditiveBlending,
      depthWrite: false,
    }),
  );
  mesh.position.copy(center);
  mesh.frustumCulled = false;
  return mesh;
}

/** Orbiting crystal shards: three hue-tinted InstancedMeshes of emissive octahedra. */
function makeShards(
  center: Vector3,
  rng: () => number,
): { meshes: InstancedMesh[]; mats: MeshStandardMaterial[] } {
  const hues = [0x8fd4ff, 0xc59cff, 0x8af0d8];
  const geo = new OctahedronGeometry(1, 0);
  const dummy = new Object3D();
  const meshes: InstancedMesh[] = [];
  const mats: MeshStandardMaterial[] = [];
  const perGroup = 50;
  for (const hue of hues) {
    const mat = emissive(hue, 0.85);
    const mesh = new InstancedMesh(geo, mat, perGroup);
    for (let i = 0; i < perGroup; i++) {
      const a = rng() * Math.PI * 2;
      const r = 2200 + rng() * 4400;
      dummy.position.set(Math.cos(a) * r, (rng() - 0.5) * 4600, Math.sin(a) * r);
      dummy.rotation.set(rng() * 6.28, rng() * 6.28, rng() * 6.28);
      dummy.scale.setScalar(110 + rng() * 260);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    mesh.position.copy(center);
    mesh.frustumCulled = false;
    meshes.push(mesh);
    mats.push(mat);
  }
  return { meshes, mats };
}

/** Will-o-wisp orbs: small bright emissive spheres scattered near the course. */
function makeWisps(
  center: Vector3,
  rng: () => number,
): { mesh: InstancedMesh; mat: MeshStandardMaterial } {
  const N = 40;
  const mat = emissive(0xa8ffe0, 1.15);
  const mesh = new InstancedMesh(new SphereGeometry(1, 10, 10), mat, N);
  const dummy = new Object3D();
  for (let i = 0; i < N; i++) {
    const a = rng() * Math.PI * 2;
    const r = 700 + rng() * 2600;
    dummy.position.set(Math.cos(a) * r, (rng() - 0.5) * 2400, Math.sin(a) * r);
    dummy.scale.setScalar(26 + rng() * 48);
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
  }
  mesh.position.copy(center);
  mesh.frustumCulled = false;
  return { mesh, mat };
}

/** Rising light shafts: soft additive cones widening upward, faking volumetric beams. */
function makeLightShafts(
  center: Vector3,
  rng: () => number,
): { group: Group; mat: MeshStandardMaterial } {
  const mat = new MeshStandardMaterial({
    color: 0xcfe6ff,
    emissive: 0xbfe0ff,
    emissiveIntensity: 0.5,
    transparent: true,
    opacity: 0.085,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide,
  });
  const geo = new CylinderGeometry(760, 90, 8200, 26, 1, true); // wide at top → beam
  const group = new Group();
  const SHAFTS = 5;
  for (let i = 0; i < SHAFTS; i++) {
    const a = (i / SHAFTS) * Math.PI * 2;
    const r = i === 0 ? 0 : 2600 + rng() * 1400;
    const cone = new Mesh(geo, mat);
    cone.position.set(Math.cos(a) * r, -600 + rng() * 600, Math.sin(a) * r);
    cone.castShadow = false;
    cone.receiveShadow = false;
    group.add(cone);
  }
  group.position.copy(center);
  return { group, mat };
}

/** Animated decor returned from addSetPieces, spun/pulsed each frame. */
interface ArcaneAnimated {
  runeGates: Mesh[];
  portal: Mesh;
  portalMat: MeshStandardMaterial;
  runeMat: MeshStandardMaterial;
  motes: Points;
  shards: InstancedMesh[];
  shardMats: MeshStandardMaterial[];
  wisps: InstancedMesh;
  wispMat: MeshStandardMaterial;
  shafts: Group;
  shaftMat: MeshStandardMaterial;
}

/** The arcane landmarks + ambient FX, anchored around the central spawn column. */
function addSetPieces(scene: Scene, spawn: Vector3): ArcaneAnimated {
  const cx = spawn.x;
  const cz = spawn.z;
  const top = spawn.y;

  // Starfield — points scattered on a big sphere so the void reads as a night
  // sky. Far out (inside the camera far plane), additive, never collidable.
  const STARS = 900;
  const starPos = new Float32Array(STARS * 3);
  for (let i = 0; i < STARS; i++) {
    const u = Math.random() * 2 - 1;
    const a = Math.random() * Math.PI * 2;
    const r = 26000;
    const s = Math.sqrt(1 - u * u);
    starPos[i * 3] = cx + Math.cos(a) * s * r;
    starPos[i * 3 + 1] = top + u * r;
    starPos[i * 3 + 2] = cz + Math.sin(a) * s * r;
  }
  const starGeom = new BufferGeometry();
  starGeom.setAttribute('position', new Float32BufferAttribute(starPos, 3));
  const stars = new Points(
    starGeom,
    new PointsMaterial({
      color: 0xcfd8ff,
      size: 120,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.9,
      blending: AdditiveBlending,
      depthWrite: false,
    }),
  );
  stars.frustumCulled = false;
  scene.add(stars);

  // Heart Crystal — the central beacon spire, visible from every ramp. A tall
  // bright shard rising from the lava up through the whole course.
  decor(
    scene,
    makeSpire({ baseRadius: 300, topRadius: 24, height: 5400, sides: 7 }),
    emissive(0x9fe8ff, 0.9),
    new Vector3(cx, top - 5200, cz),
  );

  // Crystal-tower skyline (the floating "castle"): a ring of glowing spires.
  const towerMat = emissive(0x6fa8ff, 0.6);
  const towers = 9;
  for (let i = 0; i < towers; i++) {
    const a = (i / towers) * Math.PI * 2;
    const r = 4200 + (i % 3) * 520;
    const h = 1800 + (i % 4) * 760;
    decor(
      scene,
      makeSpire({ baseRadius: 230, topRadius: 70, height: h, sides: 6 }),
      towerMat,
      new Vector3(cx + Math.cos(a) * r, top - 1400 - (i % 4) * 220, cz + Math.sin(a) * r),
    );
  }

  // Floating rune gates — cyan rings around the spawn that frame the drop-ins.
  const runeMat = emissive(0x55e0ff, 1.2, 0.85);
  const runeGates: Mesh[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const r = 1150;
    const ring = decor(
      scene,
      new TorusGeometry(280, 26, 14, 40),
      runeMat,
      new Vector3(cx + Math.cos(a) * r, top - 60 - i * 36, cz + Math.sin(a) * r),
    );
    ring.lookAt(cx, ring.position.y, cz); // open toward the centre
    runeGates.push(ring);
  }

  // The Translocation Portal — a big swirling violet halo over the academy.
  const portalMat = emissive(0xb46bff, 1.3, 0.78);
  const portal = decor(
    scene,
    new TorusGeometry(640, 72, 20, 64),
    portalMat,
    new Vector3(cx, top + 1000, cz),
  );
  portal.rotation.x = Math.PI / 2;

  // Ambient mystical FX, scattered (deterministically) around the mid-course.
  const drng = mulberry32(DECOR_SEED);
  const fxCenter = new Vector3(cx, top - 1500, cz);
  const motes = makeMotes(fxCenter, drng);
  scene.add(motes);
  const { meshes: shards, mats: shardMats } = makeShards(fxCenter, drng);
  for (const s of shards) {
    scene.add(s);
  }
  const { mesh: wisps, mat: wispMat } = makeWisps(fxCenter, drng);
  scene.add(wisps);
  const { group: shafts, mat: shaftMat } = makeLightShafts(fxCenter, drng);
  scene.add(shafts);

  return {
    runeGates,
    portal,
    portalMat,
    runeMat,
    motes,
    shards,
    shardMats,
    wisps,
    wispMat,
    shafts,
    shaftMat,
  };
}

/**
 * Builds a free-surf generated course skinned in the arcane crystal set: a seeded
 * `randomSpec` backbone (which fans into several surf lanes past 200 features),
 * re-skinned per lane into one of three crystal hues, plus the arcane set-pieces +
 * ambient FX.
 */
async function buildGeneratedArcane(
  scene: Scene,
  mapId: string,
  name: string,
  featureCount: number,
  seed: number,
): Promise<BuiltLevel> {
  const materials = await loadArcaneMaterials();
  const spec = randomSpec(featureCount, mulberry32(seed));
  spec.id = mapId;
  spec.name = name;
  const level = buildLevelFromPlacements(spec, scene, materials);

  // Surf variety: re-skin each ramp mesh with one of three crystal-hue variants,
  // bucketed by its angle around the central column so each radiating lane reads
  // as a distinct crystal. Done before the merge so it stays a few draw calls.
  const variants = makeRampVariants(materials.ramp as MeshStandardMaterial);
  const cx = level.spawn.x;
  const cz = level.spawn.z;
  for (const m of level.colliders) {
    if ((m.material as MeshStandardMaterial) === materials.ramp) {
      const ang = Math.atan2(m.position.z - cz, m.position.x - cx) + Math.PI;
      const bucket = Math.min(
        variants.length - 1,
        Math.floor((ang / (Math.PI * 2)) * variants.length),
      );
      m.material = variants[bucket] as MeshStandardMaterial;
    }
  }

  // Perf: collapse the hundreds of per-ramp meshes into one per material.
  const colliders = mergeStaticMeshes(scene, level.colliders);
  const anim = addSetPieces(scene, level.spawn);

  // Spin/pulse the animated decor (real-time, dt-scaled).
  let t = 0;
  const update = (dt: number): void => {
    t += dt;
    for (let i = 0; i < anim.runeGates.length; i++) {
      anim.runeGates[i]?.rotateZ(dt * (0.4 + i * 0.05));
    }
    anim.portal.rotateZ(dt * 0.5);
    anim.runeMat.emissiveIntensity = 1.0 + 0.4 * Math.sin(t * 2.2);
    anim.portalMat.emissiveIntensity = 1.1 + 0.4 * Math.sin(t * 1.4);

    anim.motes.rotateY(dt * 0.02); // slow drift of the mote cloud
    for (let i = 0; i < anim.shards.length; i++) {
      anim.shards[i]?.rotateY(dt * (0.05 + i * 0.015)); // crystals orbit the column
      const sm = anim.shardMats[i];
      if (sm) {
        sm.emissiveIntensity = 0.85 + 0.3 * Math.sin(t * 1.6 + i);
      }
    }
    anim.wisps.rotateY(-dt * 0.06);
    anim.wispMat.emissiveIntensity = 1.0 + 0.5 * Math.sin(t * 3.0);
    anim.shafts.rotateY(dt * 0.015);
    anim.shaftMat.emissiveIntensity = 0.45 + 0.2 * Math.sin(t * 0.9);
  };

  return { ...level, colliders, mapId, update };
}

export function buildArcaneAcademy(scene: Scene): Promise<BuiltLevel> {
  return buildGeneratedArcane(scene, 'surf_arcane_academy', 'Arcane Academy', FEATURE_COUNT, SEED);
}
